import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { normalizeBarTimeSec } from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  type ChartSeriesSource,
  SERIES_MIN_TIMEFRAME_SECONDS,
  type SeriesPoint,
} from '@/lib/tradingview/datafeeds/series-sources'
import { fetchOptionsVolumeSeries } from '@/service/options-analytics.api'
import { OptionsVolumeMeasure, type OptionsVolumeSeries } from '@/types/generated/public_api'

export function resolveOptionsVolumeMeasure(
  inputs: Record<string, string | number | boolean> | undefined
): OptionsVolumeMeasure {
  const isPremium = inputs?.valueType === 'premium'
  const isQuote = inputs?.measure === 'quote'
  if (isPremium) {
    return isQuote
      ? OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_QUOTE
      : OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_BASE
  }
  return isQuote
    ? OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_QUOTE
    : OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_BASE
}

export function optionsVolumeSeriesToSeriesPoints(
  series: OptionsVolumeSeries | undefined
): SeriesPoint[] {
  if (!series) return []
  const points: SeriesPoint[] = []
  for (const bucket of series.buckets) {
    const timeSec = normalizeBarTimeSec(Number(bucket.timestampMs))
    const volume = Number(bucket.volume)
    if (!Number.isFinite(timeSec) || !Number.isFinite(volume)) continue
    points.push({ timeSec, values: { optionsVolume: volume } })
  }
  return points
}

export const optionsVolumeSeriesSource: ChartSeriesSource = {
  id: 'optionsVolume',
  metrics: ['optionsVolume'],
  // v1 = bar-close poll. A push stream exists (use-options-volume-stream.ts)
  // but is a React hook — wiring it needs a headless adapter that pipes the
  // stream into the manager via subscribeLive (slot implemented + tested).
  refresh: 'bar-close',
  minTimeframeSeconds: SERIES_MIN_TIMEFRAME_SECONDS,
  fetchHistory: async (ctx, signal) => {
    const series = await fetchOptionsVolumeSeries(
      {
        exchange: ctx.exchange,
        underlying: resolveBaseCurrency([], ctx.symbol, null),
        timeframe: ctx.timeframeSeconds,
        volumeMeasure: resolveOptionsVolumeMeasure(ctx.inputs),
        from: String(ctx.fromSec * 1000),
        to: String(ctx.toSec * 1000),
      },
      signal
    )
    return optionsVolumeSeriesToSeriesPoints(series)
  },
}
